import {useTranslation} from '@aboutyou/i18n';
import React, {FC, PropsWithChildren} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../../../constants/typography';
import {FilterCard} from '../../../../atoms/FilterCard/FilterCard';
import {Icon} from '../../../../atoms/Icon/Icon';
import {GenericIconName} from '../../../../atoms/Icon/IconName';
import {FilterAccordion} from '../../FilterAccordion/FilterAccordion';

export type GridType = 'single' | 'multi';
export type ProductImageType = 'model' | 'product';

interface Props {
  gridType: GridType;
  onSetGridType: (gridType: GridType) => void;
  onSetProductImageType: (gridType: ProductImageType) => void;
  productImageType?: ProductImageType;
  enableFlyoutFilter: boolean;
  withBottomMargin?: boolean;
}

const ToggleIcon = ({isOpen}: {isOpen: boolean}) => {
  return (
    <Icon
      data-testid="accordionToggleIcon"
      size="XSS"
      asset={isOpen ? GenericIconName.ArrowUp : GenericIconName.ArrowDown}
      fill={COLORS.Grey1}
    />
  );
};

const FilterWrapper = (props: PropsWithChildren<Props>) => {
  const {
    enableFlyoutFilter,
    children,
    productImageType,
    gridType,
    withBottomMargin,
  } = props;
  const {t} = useTranslation();

  if (enableFlyoutFilter) {
    return (
      <FilterAccordion
        content={t('View')}
        subTitle={`${productImageType === 'model' ? t('Model') : t('Item')}, ${
          gridType === 'multi' ? t('Grid') : t('Single')
        }`}
        ToggleIcon={ToggleIcon}
        toggleContent={children as React.ReactElement}
        withoutBottomBorder={withBottomMargin}
      />
    );
  }

  return <FilterCard title={t('View')}>{children}</FilterCard>;
};

export const FilterFlyoutViewOptions: FC<Props> = (props: Props) => {
  const {
    productImageType,
    onSetProductImageType,
    gridType,
    onSetGridType,
    enableFlyoutFilter,
    withBottomMargin,
  } = props;

  const {t} = useTranslation();

  return (
    <Section
      data-testid="filterFlyoutSectionView"
      $topMargin={enableFlyoutFilter}
      $marginBottom={withBottomMargin}
    >
      <FilterWrapper {...props}>
        <SwitchWrapper data-testid="filterFlyoutView">
          <SwitchButtonGroup
            aria-labelledby={`${t('View')} - ${t('Product images')}`}
            role="radiogroup"
          >
            <SwitchButton
              active={productImageType === 'product'}
              aria-checked={productImageType === 'product'}
              data-testid="filterFlyoutViewButtonProduct"
              onClick={() =>
                onSetProductImageType('product' as ProductImageType)
              }
              role="radio"
              $smallerBorder={enableFlyoutFilter}
            >
              {t('Item')}
            </SwitchButton>
            <SwitchButton
              active={productImageType === 'model'}
              aria-checked={productImageType === 'model'}
              data-testid="filterFlyoutViewButtonModel"
              onClick={() => onSetProductImageType('model' as ProductImageType)}
              role="radio"
              $smallerBorder={enableFlyoutFilter}
            >
              {t('Model')}
            </SwitchButton>
          </SwitchButtonGroup>
          <SwitchButtonGroup
            aria-labelledby={`${t('View')} - ${t('Columns')}`}
            role="radiogroup"
          >
            <SwitchButton
              active={gridType === 'multi'}
              aria-checked={gridType === 'multi'}
              data-testid="filterFlyoutViewButtonMulti"
              onClick={() => onSetGridType('multi' as GridType)}
              role="radio"
              $smallerBorder={enableFlyoutFilter}
            >
              <GridSwitchIcon
                asset={GenericIconName.GridMultiCol}
                data-testid="filterFlyoutView"
                size={'XS'}
              />
            </SwitchButton>
            <SwitchButton
              active={gridType === 'single'}
              aria-checked={gridType === 'single'}
              data-testid="filterFlyoutViewButtonSingle"
              onClick={() => onSetGridType('single' as GridType)}
              role="radio"
              $smallerBorder={enableFlyoutFilter}
            >
              <GridSwitchIcon
                asset={GenericIconName.GridSingleCol}
                data-testid="filterFlyoutView"
                size={'XS'}
              />
            </SwitchButton>
          </SwitchButtonGroup>
        </SwitchWrapper>
      </FilterWrapper>
    </Section>
  );
};

const Section = styled.div<{$topMargin?: boolean; $marginBottom?: boolean}>`
  ${props => (props.$topMargin ? `border-top: 4px solid ${COLORS.Grey4};` : '')}
  ${props =>
    props.$marginBottom ? `border-bottom: 4px solid ${COLORS.Grey4};` : ''}
`;

const SwitchWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.375rem;
`;

const SwitchButton = styled.button<{
  active?: boolean;
  $smallerBorder?: boolean;
}>`
  box-sizing: border-box;
  height: 38px;
  padding: 0 0.875rem;
  border: ${({active, $smallerBorder}) =>
    active
      ? `${$smallerBorder ? '1px' : '2px'} solid ${COLORS.Black}`
      : `${$smallerBorder ? '1px' : '2px'} solid ${COLORS.Grey5}`};
  border-radius: ${props => (props.$smallerBorder ? '4px' : '2px')};
  margin-right: 0.75rem;
  background-color: ${({active}) => (active ? COLORS.White : COLORS.Grey5)};
  color: ${COLORS.Black};
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.bold};
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;

  &:focus {
    outline: none;
  }

  &:hover {
    background-color: ${({active}) => (active ? COLORS.White : COLORS.Grey4)};
    border-color: ${({active}) => (active ? COLORS.Black : COLORS.Grey4)};
  }
`;

const SwitchButtonGroup = styled.div`
  display: flex;
  align-items: center;

  ${SwitchButton} {
    &:last-child {
      margin-right: 0;
    }
  }
`;

const GridSwitchIcon = styled(Icon)`
  fill: ${COLORS.Black};
`;
